import clsx from "clsx";
import gsap from "gsap";
import { useWindowScroll } from "react-use";
import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";

const navItems = ["Home", "About", "Contact"];

const NavBar = () => {
  // State for toggling audio and visual indicator
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isIndicatorActive, setIsIndicatorActive] = useState(false);

  // Refs for audio and navigation container
  const audioElementRef = useRef(null);
  const navContainerRef = useRef(null);

  const { y: currentScrollY } = useWindowScroll();
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [navState, setNavState] = useState("hero"); // "hero", "transition", "about"
  const [scrollDirection, setScrollDirection] = useState("none"); // "up", "down", "none"

  // Toggle audio and visual indicator
  const toggleAudioIndicator = () => {
    setIsAudioPlaying((prev) => !prev);
    setIsIndicatorActive((prev) => !prev);
  };

  // Manage audio playback
  useEffect(() => {
    if (isAudioPlaying) {
      audioElementRef.current.play();
    } else {
      audioElementRef.current.pause();
    }
  }, [isAudioPlaying]);


  useEffect(() => {
    const heroHeight = window.innerHeight; // Height of hero section
    const transitionEnd = heroHeight + 400; // End transition 400px into about section
    
    // Determine scroll direction
    if (currentScrollY > lastScrollY) {
      setScrollDirection("down");
    } else if (currentScrollY < lastScrollY) {
      setScrollDirection("up");
    }

    // Determine nav state based on position
    if (currentScrollY === 0) {
      // At the very top
      setIsNavVisible(true);
      setNavState("hero");
    } else if (currentScrollY < heroHeight) {
      // In hero section
      setNavState("hero");
      if (scrollDirection === "down") {
        setIsNavVisible(false);
      } else {
        setIsNavVisible(true);
      }
    } else if (currentScrollY >= heroHeight && currentScrollY < transitionEnd) {
      // Past hero section
      setNavState("transition");
      if (scrollDirection === "down") {
        setIsNavVisible(false);
      } else {
        setIsNavVisible(true);
      }
    } else {
      // In about section and beyond
      setNavState("about");
      if (scrollDirection === "down") {
        setIsNavVisible(false);
      } else {
        setIsNavVisible(true);
      }
    }

    // Handle background visibility
    if (scrollDirection === "up" && currentScrollY > 0) {
      // Always show dark background when scrolling up
      navContainerRef.current.classList.add("floating-nav");
      navContainerRef.current.classList.remove("floating-nav-transparent");
    } else if (navState === "hero") {
      // Show dark background in hero
      navContainerRef.current.classList.add("floating-nav");
      navContainerRef.current.classList.remove("floating-nav-transparent");
    } else if (navState === "transition" || navState === "about") {
      // Fade out background in transition/about when scrolling down
      navContainerRef.current.classList.remove("floating-nav");
      navContainerRef.current.classList.add("floating-nav-transparent");
    }

    setLastScrollY(currentScrollY);
  }, [currentScrollY, lastScrollY, scrollDirection, navState]);

  useEffect(() => {
    gsap.to(navContainerRef.current, {
      y: isNavVisible ? 0 : -100,
      opacity: isNavVisible ? 1 : 0,
      duration: 0.2,
    });
  }, [isNavVisible]);

  return (
    <div
      ref={navContainerRef}
      className="fixed inset-x-0 top-4 z-50 h-16 border-none transition-all duration-700 sm:inset-x-6"
    >
      <header className="absolute top-1/2 w-full -translate-y-1/2">
        <nav className="grid size-full grid-cols-3 items-center p-2 md:flex md:justify-between md:p-4">
          {/* Logo - Left on mobile and desktop */}
          <div className="flex items-center justify-start">
            <img 
              src="/img/logo.png" 
              alt="logo" 
              className={clsx(
                "w-8 md:w-10 transition-all duration-300",
                {
                  "brightness-100": navState === "hero" || scrollDirection === "up" || (navState === "about" && scrollDirection === "down"),
                  "brightness-0": navState === "transition" && scrollDirection === "down",
                }
              )}
            />
          </div>

          {/* Navigation Links - Center on mobile, right on desktop */}
          <div className="flex h-full items-center justify-center md:ml-auto">
            <div className="flex gap-3 md:gap-5">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item === "Home" ? "#" : `#${item.toLowerCase()}`}
                  className={clsx(
                    "relative cursor-pointer font-general text-xs uppercase transition-colors duration-300",
                    "after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0",
                    "after:transition-transform after:duration-300 after:ease-[cubic-bezier(0.65_0.05_0.36_1)]",
                    "hover:after:origin-bottom-left hover:after:scale-x-100",
                    "md:text-base",
                    navState === "hero" ? "text-white after:bg-white" :
                    navState === "transition" && scrollDirection === "down" ? "text-[#090909] after:bg-[#090909]" :
                    navState === "about" && scrollDirection === "down" ? "text-white after:bg-white" :
                    scrollDirection === "up" ? "text-white after:bg-white" :
                    "text-white after:bg-white"
                  )}
                >
                  {item}
                </a>
              ))}
            </div>
          </div>

          {/* Audio and AI buttons - Right on mobile and desktop */}
          <div className="flex h-full items-center justify-end gap-1">
            <button
              onClick={toggleAudioIndicator}
              className="flex items-center space-x-0.5"
            >
              <audio
                ref={audioElementRef}
                className="hidden"
                src="/audio/loop.mp3"
                loop
              />
              {[1, 2, 3, 4].map((bar) => (
                <div
                  key={bar}
                  className={clsx(
                    "h-1 w-px rounded-full transition-all duration-200 ease-in-out",
                    navState === "hero" ? "bg-white" :
                    navState === "transition" && scrollDirection === "down" ? "bg-[#090909]" :
                    navState === "about" && scrollDirection === "down" ? "bg-white" :
                    scrollDirection === "up" ? "bg-white" :
                    "bg-white",
                    isIndicatorActive && "active"
                  )}
                  style={{
                    animationDelay: `${bar * 0.1}s`,
                  }}
                />
              ))}
            </button>
            <a
              onClick={() => {
                const event = new CustomEvent('openChatbot');
                window.dispatchEvent(event);
              }}
              className={clsx(
                "ml-1 cursor-pointer font-general text-xs uppercase transition-colors duration-300",
                "hover:opacity-80",
                "md:text-base md:ml-4",
                navState === "hero" ? "text-white" :
                navState === "transition" && scrollDirection === "down" ? "text-[#090909]" :
                navState === "about" && scrollDirection === "down" ? "text-white" :
                scrollDirection === "up" ? "text-white" :
                "text-white"
              )}
            >
              <span className="hidden md:inline">Ask AI</span>
              <span className="md:hidden">AI</span>
            </a>
            <button
              onClick={() => {
                const event = new CustomEvent('openChatbot');
                window.dispatchEvent(event);
              }}
              className={clsx(
                "ml-1 rounded-full p-1.5 transition-all duration-300",
                "hover:rotate-12 hover:scale-110",
                "md:ml-4 md:p-2",
                navState === "hero" ? "text-white hover:bg-white/10" :
                navState === "transition" && scrollDirection === "down" ? "text-[#090909] hover:bg-black/10" :
                navState === "about" && scrollDirection === "down" ? "text-white hover:bg-white/10" :
                scrollDirection === "up" ? "text-white hover:bg-white/10" :
                "text-white hover:bg-white/10"
              )}
              aria-label="Open AI Assistant"
            >
              <MessageCircle className="size-4 md:size-5" />
            </button>
          </div>
        </nav>
      </header>
    </div>
  );
};

export default NavBar;
